import PropTypes from 'prop-types';
import { useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faDotCircle } from '@fortawesome/pro-regular-svg-icons';
import { gql, useQuery } from '@apollo/client';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useIsomorphicEffect } from 'rooks';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import cx from 'classnames';

import config from 'config';
import { requiredEmail } from 'lib/validators';
import { useAuth } from 'context/Auth';
import { useGlobal } from 'context/Global';
import CreateAccountForm from 'components/global/CreateAccountForm';
import Button from 'components/common/Button';
import FormTitle from 'components/form/FormTitle';
import FormFieldGroup from 'components/form/FormFieldGroup';
import FormLabeledInput from 'components/form/FormLabeledInput';
import FormNode from 'components/form/FormNode';
import FormNote from 'components/form/FormNote';
import FacebookLogin from './FacebookLogin';
import LoginForm from './LoginForm';

const USER_EXISTS = gql`
  query UserExists($email: String!) {
    userExists(email: $email)
  }
`;

const validationSchema = yup.object().shape({
  email: requiredEmail,
});

const EstablishUserForm = ({ initialEmail, onContinue }) => {
  const { addToast } = useGlobal();
  const { isLoggedIn } = useAuth();

  const [view, setView] = useState('default');
  const email = useRef(initialEmail ?? null);

  const { control, formState, handleSubmit } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: initialEmail ?? '',
    },
  });
  const { errors, isSubmitting } = formState;

  const { refetch: userExistsWithEmail } = useQuery(USER_EXISTS, { skip: true });
  const onSubmit = useCallback(
    async (values) => {
      try {
        email.current = values.email;
        const response = await userExistsWithEmail({ email: email.current });
        setView(response.data.userExists ? 'login' : 'register');
      } catch (err) {
        if (config('/debug')) console.error(err);
        addToast({
          id: 'workflowWelcome',
          type: 'error',
          duration: 'long',
          message: 'There was an issue looking up your email address. Please try again later.',
        });
      }
    },
    [userExistsWithEmail, addToast]
  );

  useIsomorphicEffect(() => {
    if (isLoggedIn) onContinue();
  }, [isLoggedIn, onContinue]);

  if (isLoggedIn) return null;

  return (
    <div className="container my-10 md:my-16">
      <div className="sm:mx-auto sm:max-w-md">
        {view === 'default' && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={cx('py-6 sm:p-8 sm:bg-white sm:rounded-xl sm:shadow-xl')}
          >
            <FormTitle as="h1" className="text-left mb-6 mt-2 text-2xl">
              Welcome, enter your email address to get started
            </FormTitle>
            <FormFieldGroup>
              <FormNode>
                <div className="flex justify-between w-full">
                  <Controller
                    control={control}
                    name="email"
                    render={({ field, fieldState }) => (
                      <FormLabeledInput
                        type="email"
                        isRequired
                        label="Email address"
                        className="w-10/12"
                        status={fieldState.error ? 'error' : 'default'}
                        {...field}
                      />
                    )}
                  />
                  <Button
                    as="button"
                    type="submit"
                    color="green"
                    className="font-medium w-1/12 -my-px ml-2"
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon icon={isSubmitting ? faDotCircle : faArrowRight} />
                  </Button>
                </div>
                <ErrorMessage
                  errors={errors}
                  name="email"
                  render={({ message }) => <FormNote status="error">{message}</FormNote>}
                />
              </FormNode>
            </FormFieldGroup>
            <div className="flex gap-x-4 items-center my-8">
              <div className="h-px bg-gray-400 grow" />
              <p className="shrink-0 uppercase font-medium text-xs">or</p>
              <div className="h-px bg-gray-400 grow" />
            </div>
            <FacebookLogin label="Continue with Facebook" />
          </form>
        )}
        {view === 'login' && <LoginForm initialEmail={email.current} title="Please sign in" />}
        {view === 'register' && <CreateAccountForm initialEmail={email.current} />}
      </div>
    </div>
  );
};

EstablishUserForm.propTypes = {
  initialEmail: PropTypes.string,
  onContinue: PropTypes.func.isRequired,
};

EstablishUserForm.defaultProps = {
  initialEmail: null,
};

export default EstablishUserForm;
